import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api/api';
import { useAuth } from '../App';

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const intendedPlan = searchParams.get('plan'); // carried from /pricing
  const intendedRole = searchParams.get('role') === 'driver' ? 'driver' : 'restaurant';

  const [form, setForm] = useState({
    role: intendedRole,
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    businessName: '',
  });
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authAPI.register({ ...form, ageConfirmed, smsConsent });

      // No auto-login: under hard email verification the user must click the
      // link in their email, then sign in. Show the "check your email" screen.
      setRegistered(true);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details?.[0]?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  if (registered) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="mb-8 text-center">
          <h1 className="text-5xl font-black text-yellow-400" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</h1>
          <p className="text-gray-500 text-sm">CarFleet &amp; Chauffeur Service</p>
        </div>
        <div className="w-full max-w-md bg-gray-900 rounded-2xl border border-gray-800 p-8 text-center">
          <div className="text-4xl mb-4">📬</div>
          <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
          <p className="text-gray-400 text-sm mb-2">
            We sent a verification link to <span className="text-gray-200">{form.email}</span>.
          </p>
          <p className="text-gray-400 text-sm mb-4">
            Click it to activate your account, then sign in to get started.
          </p>
          <p className="text-gray-500 text-xs mb-6">Don't see it? Check your spam or junk folder.</p>
          <Link to="/login" className="block w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition">
            Go to Sign In &rarr;
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4 py-12">
      {/* Logo */}
      <div className="mb-8 text-center">
        <h1
          className="text-5xl font-black text-yellow-400"
          style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
        >
          STARFUEL
        </h1>
        <p className="text-gray-500 text-sm">CarFleet & Chauffeur Service</p>
      </div>

      <div className="w-full max-w-md bg-gray-900 rounded-2xl border border-gray-800 p-8">
        <h2 className="text-2xl font-bold text-white mb-6">Create your account</h2>

        {/* Role Toggle */}
        <div className="flex rounded-xl bg-black border border-gray-700 p-1 mb-6">
          {[
            { key: 'restaurant', label: '🍽 Restaurant' },
            { key: 'driver', label: '🚗 Driver' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setField('role', key)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                form.role === key
                  ? 'bg-yellow-400 text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                required
                className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                placeholder="Maya"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                required
                className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                placeholder="Williams"
              />
            </div>
          </div>

          {form.role === 'restaurant' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Business Name</label>
              <input
                type="text"
                value={form.businessName}
                onChange={(e) => setField('businessName', e.target.value)}
                className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                placeholder="Maya's Soul Kitchen"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              required
              className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
              placeholder="you@business.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
              placeholder="9185550123"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setField('password', e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
              placeholder="Min 8 chars, include a number"
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
              {error}
            </div>
          )}
<label className="flex items-start gap-3 cursor-pointer mt-2">
  <input type="checkbox" checked={agreed} onChange={e => 
setAgreed(e.target.checked)} className="mt-1 w-4 h-4 accent-yellow-400 
flex-shrink-0" />
       <span className="text-xs text-gray-400 leading-relaxed">
           I agree to the <a href="/terms" target="_blank" className="text-yellow-400 hover:underline">Terms of Service</a>, <a href="/privacy" target="_blank" className="text-yellow-400 hover:underline">Privacy Policy</a>, and the {form.role === 'driver' ? <a href="/driver-agreement" target="_blank" className="text-yellow-400 hover:underline">Driver Independent Contractor Agreement</a> : <a href="/restaurant-agreement" target="_blank" className="text-yellow-400 hover:underline">Restaurant Service Agreement</a>}.
         </span>
       </label>

          <label className="flex items-start gap-3 cursor-pointer mt-2">
            <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-yellow-400 flex-shrink-0" />
            <span className="text-xs text-gray-400 leading-relaxed">
              I agree to receive SMS text messages from Starfuel CarFleet about delivery assignments, pickups, and delivery status at the phone number I provided. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for help.
            </span>
          </label>

          {form.role === 'driver' && (
            <label className="flex items-start gap-3 cursor-pointer mt-2">
              <input type="checkbox" checked={ageConfirmed} onChange={e => setAgeConfirmed(e.target.checked)} className="mt-1 w-4 h-4 accent-yellow-400 flex-shrink-0" />
              <span className="text-xs text-gray-400 leading-relaxed">
                I confirm that I am 18 years of age or older.
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={loading || !agreed || (form.role === 'driver' && !ageConfirmed)}
            className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition-all active:scale-95 disabled:opacity-50 mt-2"
          >
            {loading ? 'Creating account...' : `Join as ${form.role === 'restaurant' ? 'Restaurant' : 'Driver'}`}
          </button>
        </form>

        <p className="mt-5 text-center text-gray-400 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-yellow-400 hover:text-yellow-300 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
